/**
 * Storage Utils
 * 基礎 Storage 工具，提供 tenant 隔離、過期處理、Zod 驗證、防禦性讀取
 */

import { z } from 'zod';

/**
 * 帶有過期時間的儲存資料結構
 */
interface StoredData<T> {
  value: T;
  timestamp: number;
  expiresIn?: number; // 毫秒
}

/**
 * Storage 操作選項
 */
interface StorageOptions {
  /**
   * 資料範圍：'global' 表示全域（不加 tenant 前綴），'tenant' 表示租戶隔離（預設）
   */
  scope?: 'global' | 'tenant';
  /**
   * 過期時間（毫秒）
   */
  expiresIn?: number;
  /**
   * Zod Schema 用於驗證資料結構
   */
  schema?: z.ZodType<unknown>;
}

/**
 * 取得當前 tenantId
 * @returns tenantId 或 null
 */
export function getTenantId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return localStorage.getItem('tenant_id');
}

/**
 * 建立 Storage Key（自動加上 tenant 前綴）
 * @param key - 原始 key
 * @param scope - 資料範圍，'global' 不加前綴，'tenant' 加上 `t_{tenantId}_` 前綴
 * @returns 完整的 Storage Key
 */
export function buildStorageKey(
  key: string,
  scope: 'global' | 'tenant' = 'tenant'
): string {
  if (scope === 'global') {
    return `global_${key}`;
  }

  const tenantId = getTenantId();
  if (!tenantId) {
    // 沒有 tenantId 時，使用 'no_tenant' 作為前綴，避免資料混亂
    console.warn('[storageUtils] 沒有 tenantId，使用 no_tenant 前綴:', key);
    return `t_no_tenant_${key}`;
  }

  return `t_${tenantId}_${key}`;
}

/**
 * 儲存資料到 localStorage（可選過期時間和 Schema 驗證）
 * @param key - Storage Key（不含前綴）
 * @param value - 要儲存的值
 * @param options - 儲存選項
 */
export function setWithExpiry<T>(
  key: string,
  value: T,
  options: StorageOptions = {}
): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const { scope = 'tenant', expiresIn, schema } = options;

    // 如果提供了 Schema，先驗證資料
    // 注意：對於有 transform 的 schema（如 Set），我們需要特殊處理
    if (schema) {
      // 如果值是 Set 類型，直接轉換為陣列並儲存（不驗證，因為 Set 已經存在）
      // 驗證會在讀取時進行
      if (value instanceof Set) {
        value = Array.from(value) as unknown as T;
      } else {
        // 檢查 value 是否為函數或其他無效類型
        if (typeof value === 'function') {
          console.error(
            `[storageUtils] 無法儲存函數類型的值 key "${key}"，跳過儲存`
          );
          return;
        }

        // 對於非 Set 類型，正常驗證
        const result = schema.safeParse(value);
        if (!result.success) {
          console.error(
            `[storageUtils] 資料驗證失敗，無法儲存 key "${key}":`,
            result.error
          );
          return;
        }
        // 使用驗證後的資料（可能經過 transform）
        value = result.data as T;
      }
    }

    // 建立儲存資料結構
    const data: StoredData<T> = {
      value,
      timestamp: Date.now(),
      expiresIn,
    };

    // 建立完整的 Storage Key
    const storageKey = buildStorageKey(key, scope);

    // 儲存到 localStorage
    localStorage.setItem(storageKey, JSON.stringify(data));
  } catch (error) {
    console.error(
      `[storageUtils] 儲存資料失敗 key "${key}":`,
      error instanceof Error ? error.message : String(error)
    );
  }
}

/**
 * 從 localStorage 讀取資料（帶驗證和預設值）
 * @param key - Storage Key（不含前綴）
 * @param defaultValue - 預設值（當資料不存在或驗證失敗時使用）
 * @param options - 讀取選項
 * @returns 讀取的值或預設值
 */
export function getWithExpiry<T>(
  key: string,
  defaultValue: T,
  options: StorageOptions = {}
): T {
  if (typeof window === 'undefined') {
    return defaultValue;
  }

  try {
    const { scope = 'tenant', schema } = options;

    // 建立完整的 Storage Key
    const storageKey = buildStorageKey(key, scope);

    // 從 localStorage 讀取
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      return defaultValue;
    }

    // 解析 JSON
    let data: StoredData<T>;
    try {
      data = JSON.parse(stored) as StoredData<T>;
    } catch (parseError) {
      console.warn(
        `[storageUtils] JSON 解析失敗 key "${key}"，使用預設值:`,
        parseError
      );
      return defaultValue;
    }

    // 檢查是否過期
    if (data.expiresIn && data.timestamp) {
      const now = Date.now();
      const age = now - data.timestamp;
      if (age > data.expiresIn) {
        // 資料已過期，清除並返回預設值
        console.log(
          `[storageUtils] 資料已過期 key "${key}" (${age}ms > ${data.expiresIn}ms)，清除資料`
        );
        localStorage.removeItem(storageKey);
        return defaultValue;
      }
    }

    // 如果提供了 Schema，驗證資料結構
    if (schema) {
      const result = schema.safeParse(data.value);
      if (!result.success) {
        console.warn(
          `[storageUtils] 資料驗證失敗 key "${key}"，使用預設值:`,
          result.error
        );
        // 清除無效資料
        localStorage.removeItem(storageKey);
        return defaultValue;
      }
      // 返回驗證後的資料（可能經過 transform）
      return result.data as T;
    }

    return data.value;
  } catch (error) {
    console.error(
      `[storageUtils] 讀取資料失敗 key "${key}"，使用預設值:`,
      error instanceof Error ? error.message : String(error)
    );
    return defaultValue;
  }
}

/**
 * 從 localStorage 移除資料
 * @param key - Storage Key（不含前綴）
 * @param scope - 資料範圍
 */
export function removeItem(
  key: string,
  scope: 'global' | 'tenant' = 'tenant'
): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const storageKey = buildStorageKey(key, scope);
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.error(
      `[storageUtils] 移除資料失敗 key "${key}":`,
      error instanceof Error ? error.message : String(error)
    );
  }
}

/**
 * 清除特定 tenant 的所有資料
 * @param tenantId - 租戶 ID
 */
export function clearTenantData(tenantId: string): void {
  if (typeof window === 'undefined' || !tenantId) {
    return;
  }

  try {
    const prefix = `t_${tenantId}_`;
    const keysToRemove: string[] = [];

    // 收集所有屬於該租戶的 key
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }

    // 移除所有收集到的 key
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });

    console.log(
      `[storageUtils] 已清除租戶 ${tenantId} 的 ${keysToRemove.length} 筆資料`
    );
  } catch (error) {
    console.error(
      `[storageUtils] 清除租戶資料失敗 tenantId "${tenantId}":`,
      error instanceof Error ? error.message : String(error)
    );
  }
}
